import React, { useEffect, useMemo, useState } from "react";
import { Card } from "@/components/ui/card";
import { api } from "@/lib/api";
import { BarChart, Bar, ResponsiveContainer, XAxis, YAxis, Tooltip, CartesianGrid } from "recharts";
import { Rewind, Flag, RefreshCw, XCircle, Check } from "lucide-react";

const eventColor = {
  answer: "bg-emerald-500",
  change: "bg-orange-500",
  mark_review: "bg-amber-500",
  unmark_review: "bg-stone-400",
  skip: "bg-rose-500",
};
const eventIcon = {
  answer: Check,
  change: RefreshCw,
  mark_review: Flag,
  unmark_review: Flag,
  skip: XCircle,
};

export default function ExamReplay({ attemptId }) {
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get(`/attempts/${attemptId}/replay`).then((r) => setData(r.data)).catch(() => {});
  }, [attemptId]);

  const chartData = useMemo(() => {
    if (!data) return [];
    return (data.per_question_time || []).map((q) => ({
      name: `Q${q.number}`,
      seconds: Math.round((q.time_ms || 0) / 1000),
      section: q.section,
    }));
  }, [data]);

  if (!data) return <div className="text-sm text-stone-500">Loading replay…</div>;

  const totalSec = data.time_used_seconds || 1;
  // Sort events by timestamp
  const events = [...(data.events || [])].sort((a, b) => (a.timestamp || "").localeCompare(b.timestamp || ""));
  const startMs = events.length ? Date.parse(events[0].timestamp) : 0;

  return (
    <div className="space-y-6">
      <Card className="p-6 rounded-md border-stone-200 shadow-none">
        <div className="flex items-center gap-3 mb-4">
          <Rewind className="h-5 w-5 text-orange-600" />
          <h3 className="font-heading text-xl font-semibold">Time per question</h3>
          <span className="ml-auto font-mono text-xs text-stone-500">
            {Math.round(totalSec / 60)}m {totalSec % 60}s total
          </span>
        </div>
        {chartData.length === 0 ? (
          <div className="text-sm text-stone-500 py-8 text-center border border-dashed border-stone-200 rounded-md">
            No telemetry captured for this attempt.
          </div>
        ) : (
          <div className="h-72">
            <ResponsiveContainer>
              <BarChart data={chartData}>
                <CartesianGrid stroke="#e7e5e4" strokeOpacity={0.5} vertical={false} />
                <XAxis dataKey="name" tick={{ fill: "#78716c", fontSize: 11, fontFamily: "JetBrains Mono" }} tickLine={false} axisLine={{ stroke: "#e7e5e4" }} />
                <YAxis unit="s" tick={{ fill: "#78716c", fontSize: 11, fontFamily: "JetBrains Mono" }} tickLine={false} axisLine={{ stroke: "#e7e5e4" }} />
                <Tooltip contentStyle={{ background: "#fff", border: "1px solid #e7e5e4", borderRadius: 6, fontSize: 12 }} />
                <Bar dataKey="seconds" fill="#ea580c" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}
      </Card>

      <Card className="p-6 rounded-md border-stone-200 shadow-none">
        <div className="flex items-center gap-3 mb-4">
          <h3 className="font-heading text-xl font-semibold">Event timeline</h3>
          <span className="ml-auto font-mono text-xs text-stone-500">{events.length} events</span>
        </div>
        <div className="flex flex-wrap gap-3 mb-4 text-xs">
          {[
            ["Answer", "bg-emerald-500"],
            ["Change", "bg-orange-500"],
            ["Mark", "bg-amber-500"],
            ["Skip", "bg-rose-500"],
          ].map(([l, c]) => (
            <div key={l} className="flex items-center gap-1.5">
              <div className={`h-3 w-3 rounded-full ${c}`} />
              <span className="text-stone-600">{l}</span>
            </div>
          ))}
        </div>
        {events.length === 0 ? (
          <div className="text-sm text-stone-500 py-8 text-center border border-dashed border-stone-200 rounded-md">
            No events captured.
          </div>
        ) : (
          <div className="max-h-96 overflow-y-auto pr-2">
            <div className="space-y-1" data-testid="replay-events">
              {events.map((e, i) => {
                const Icon = eventIcon[e.event_type] || RefreshCw;
                const t = Date.parse(e.timestamp) - startMs;
                const sec = Math.max(0, Math.round(t / 1000));
                const mm = String(Math.floor(sec / 60)).padStart(2, "0");
                const ss = String(sec % 60).padStart(2, "0");
                return (
                  <div key={`${e.timestamp}-${e.q_id}-${e.event_type}-${i}`} className="flex items-center gap-3 py-1.5 border-b border-stone-100 last:border-b-0">
                    <span className="font-mono text-xs text-stone-500 tabular-nums w-14">{mm}:{ss}</span>
                    <div className={`h-6 w-6 rounded-full flex items-center justify-center ${eventColor[e.event_type] || "bg-stone-400"}`}>
                      <Icon className="h-3 w-3 text-white" />
                    </div>
                    <span className="font-mono text-xs text-stone-500 w-10">Q{e.number || "?"}</span>
                    <span className="text-sm text-stone-800 flex-1 truncate">
                      {e.event_type === "change" && `Changed answer: ${e.previous_value || "—"} → ${e.new_value || "—"}`}
                      {e.event_type === "answer" && `Answered: ${e.new_value}`}
                      {e.event_type === "mark_review" && "Marked for review"}
                      {e.event_type === "unmark_review" && "Unmarked review"}
                      {e.event_type === "skip" && "Skipped / cleared answer"}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </Card>
    </div>
  );
}
